

<template>
  <div class="examCalendarModal">
    <div class="modal-body">
      <p class="modal-title">考试日历</p>
      <div class="modal-close" @click="handleClose"></div>
      <div class="modal-content">
        <!--考试日历-->
        <div class="calendar-box">
          <!-- 年月选择器 -->
          <div class="calendar-header">
            <div class="year-month-selector">
              <el-select v-model="selectedYear" @change="onYearMonthChange" class="date-select">
                <el-option v-for="year in yearOptions" :key="year" :value="year" :label="`${year}年`">{{ year }}年</el-option>
              </el-select>
              <el-select v-model="selectedMonth" @change="onYearMonthChange" class="date-select">
                <el-option v-for="month in monthOptions" :key="month.value" :value="month.value" :label="month.label">{{ month.label }}</el-option>
              </el-select>
            </div>
          </div>
          
          <!-- 日历网格 -->
          <div class="calendar-grid">
            <!-- 星期标题 -->
            <div class="calendar-weekdays">
              <div v-for="day in weekdays" :key="day" class="weekday">{{ day }}</div>
            </div>
            
            <!-- 加载状态 -->
            <div v-if="loading" class="calendar-loading">
              加载中...
            </div>
            
            
            <!-- 日期网格 -->
            <div v-show="!loading" class="calendar-days">
              <div 
                v-for="(day, index) in calendarDays" 
                :key="index"
                :class="[
                  'calendar-day',
                  { 
                    'is-today': day.isToday,
                    'is-current-month': day.isCurrentMonth,
                    'has-exam': day.hasExam,
                    'is-selected': day.isSelected
                  }
                ]"
                @click="onDayClick(day)"
              >
                <span class="day-number">{{ day.day }}</span>
                <span v-if="day.hasExam" class="exam-dot"></span>
              </div>
            </div>
          </div>
        </div>
        
        <!--考试列表-->
        <div class="exam-list-box">
          <template v-if="selectedDayExams.length > 0">
            <div 
              v-for="exam in selectedDayExams" 
              :key="exam.id" 
              class="exam-list-item"
            >
              <div class="exam-title">
                <span class="ic_paper"/>
                {{ exam.roundName }}
              </div>
              <div class="exam-time">
                {{ formatExamTime(exam.startTime, exam.rule?.costTime) }}
              </div>
            </div>
          </template>
          <div v-else class="exam-list-empty">
            <img src="@/common/images/empty_img.png" alt="">
            <p>当日没有安排考试喔～</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { getExamRoundList } from '@/api/qzStudent';
import {Select,Option} from "element-ui";
import { getSystemTime } from '@/api/api';
import dayjs from 'dayjs';

export default {
  name: 'ExamCalendarModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  components:{
    'el-select': Select,
    'el-option': Option,
  },
  data() {
    return {
      examList: [], // 当前月的所有考试数据
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      selectedDate: null, // 当前选中的日期
      selectedDayExams: [], // 选中日期的考试列表
      weekdays: ['日', '一', '二', '三', '四', '五', '六'],
      calendarDays: [], // 日历显示的日期数组
      yearOptions: [], // 年份选项
      loading: false, // 加载状态
      monthOptions: [
        { value: 1, label: '1月' },
        { value: 2, label: '2月' },
        { value: 3, label: '3月' },
        { value: 4, label: '4月' },
        { value: 5, label: '5月' },
        { value: 6, label: '6月' },
        { value: 7, label: '7月' },
        { value: 8, label: '8月' },
        { value: 9, label: '9月' },
        { value: 10, label: '10月' },
        { value: 11, label: '11月' },
        { value: 12, label: '12月' }
      ]
    }
  },
  watch: {
    show: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.initCalendar();
          });
        }
      },
      immediate: true
    }
  },
  created() {
    // 组件创建时立即生成基础日历数据和年份选项
    this.generateBasicCalendar();
    this.generateYearOptions();
  },
  mounted() {
    // 确保组件挂载后日历数据和年份选项正确显示
    this.$nextTick(() => {
      if (this.calendarDays.length === 0) {
        this.generateBasicCalendar();
      }
      if (this.yearOptions.length === 0) {
        this.generateYearOptions();
      }
    });
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    
    // 生成基础日历数据（不依赖异步加载）
    generateBasicCalendar() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      
      const startOfMonth = dayjs(`${year}-${month}-01`);
      const endOfMonth = startOfMonth.endOf('month');
      const startOfWeek = startOfMonth.startOf('week');
      const endOfWeek = endOfMonth.endOf('week');
      
      const days = [];
      let current = startOfWeek;
      
      while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, 'day')) {
        const dayObj = {
          day: current.date(),
          date: current.format('YYYY-MM-DD'),
          timestamp: current.valueOf(),
          isCurrentMonth: current.isSame(startOfMonth, 'month'),
          isToday: current.isSame(dayjs(), 'day'),
          hasExam: false, // 初始为false，后续会更新
          isSelected: false
        };
        
        days.push(dayObj);
        current = current.add(1, 'day');
      }
      
      this.calendarDays = days;
    },
    
    // 生成年份选项
    generateYearOptions() {
      const currentYear = new Date().getFullYear();
      this.yearOptions = [];
      // 只生成当年和往后1年
      for (let i = currentYear; i <= currentYear + 1; i++) {
        this.yearOptions.push(i);
      }
    },
    
    // 初始化日历
    async initCalendar() {
      const now = new Date();
      this.selectedYear = now.getFullYear();
      this.selectedMonth = now.getMonth() + 1;
      this.selectedDate = now.getDate();
      // 先加载考试数据，再生成日历
      await this.loadMonthExams();
      this.generateCalendarDays();
      this.updateSelectedDayExams();
    },
    
    // 加载当前月的考试数据
    async loadMonthExams() {
      this.loading = true;
      try {
        const startOfMonth = dayjs(`${this.selectedYear}-${this.selectedMonth}-01`).startOf('day');
        const endOfMonth = startOfMonth.endOf('month');
        
        const params = {
          startTime: startOfMonth.valueOf(),
          endTime: endOfMonth.valueOf()
        };
        const res = await getExamRoundList(params);
        
        if (res?.code === 200) {
          const examRounds = res.body.examRounds || [];
          this.examList = examRounds.sort((a, b) => {
            if (a.startTime === b.startTime) {
              return (b.order || 0) - (a.order || 0);
            }
            return a.startTime - b.startTime;
          });
        } else {
          this.examList = [];
        }
      } catch (error) {
        console.error('加载考试数据失败:', error);
        this.examList = [];
      } finally {
        this.loading = false;
      }
    },
    
    // 更新日历日期数组（基于考试数据）
    generateCalendarDays() {
      const startOfMonth = dayjs(`${this.selectedYear}-${this.selectedMonth}-01`);
      const endOfMonth = startOfMonth.endOf('month');
      const startOfWeek = startOfMonth.startOf('week');
      const endOfWeek = endOfMonth.endOf('week');
      
      const days = [];
      let current = startOfWeek;
      
      while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, 'day')) {
        const hasExam = this.checkDayHasExam(current);
        const dayObj = {
          day: current.date(),
          date: current.format('YYYY-MM-DD'),
          timestamp: current.valueOf(),
          isCurrentMonth: current.isSame(startOfMonth, 'month'),
          isToday: current.isSame(dayjs(), 'day'),
          hasExam: hasExam,
          isSelected: current.date() === this.selectedDate && current.isSame(startOfMonth, 'month')
        };
        
        days.push(dayObj);
        current = current.add(1, 'day');
      }
      
      this.calendarDays = days;
    },
    
    // 检查某天是否有考试
    checkDayHasExam(date) {
      if (!this.examList || this.examList.length === 0) {
        return false;
      }
      
      const dayStart = date.startOf('day').valueOf();
      const dayEnd = date.endOf('day').valueOf();
      
      return this.examList.some(exam => {
        const examTime = exam.startTime;
        return examTime >= dayStart && examTime <= dayEnd;
      });
    },
    
    // 年月改变时重新加载数据
    async onYearMonthChange() {
      // this.selectedDate = null;
      // this.selectedDayExams = [];
      await this.loadMonthExams();
      this.generateCalendarDays();
      this.updateSelectedDayExams();
    },
    
    // 点击日期
    onDayClick(day) {
      if (!day.isCurrentMonth) return;
      
      // 更新选中状态
      this.calendarDays.forEach(d => {
        d.isSelected = false;
      });
      day.isSelected = true;
      
      this.selectedDate = day.day;
      this.updateSelectedDayExams();
    },
    
    // 更新选中日期的考试列表
    updateSelectedDayExams() {
      if (!this.selectedDate) {
        this.selectedDayExams = [];
        return;
      }
      
      const selectedDay = dayjs(`${this.selectedYear}-${this.selectedMonth}-${this.selectedDate}`);
      const dayStart = selectedDay.startOf('day').valueOf();
      const dayEnd = selectedDay.endOf('day').valueOf();
      
      this.selectedDayExams = this.examList.filter(exam => {
        const examTime = exam.startTime;
        return examTime >= dayStart && examTime <= dayEnd;
      });
    },
    
    // 格式化考试时间
    formatExamTime(startTime, costTime) {
      const start = dayjs(startTime);
      const end = start.add(costTime || 0, 'minute');
      return `${start.format('HH:mm:ss')} - ${end.format('HH:mm:ss')}`;
    }
  }
}
</script>
<style scoped lang="less">
.examCalendarModal{
  position: fixed;
  z-index: 99;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  .modal-body{
    margin-top: 25vh;
    width: 800px;
    background: #FFFFFF;
    border-radius: 20px;
    padding: 40px 48px 60px;
    box-sizing: border-box;
    height: 434px;
    position: relative;
    .modal-title{
      color: #000000;
      font-weight: 600;
      font-size: 24px;
      margin-bottom: 24px;
    }
    .modal-close{
      position: absolute;
      right: 12px;
      top: 12px;
      width: 40px;
      height: 40px;
      background: url("@/common/images/ic_close2.png") center / contain no-repeat;
      cursor: pointer;
    }
    .modal-content{
      display: flex;
      align-items: flex-start;
      box-sizing: border-box;
      height: 274px;
      .calendar-box{
        width: 300px;
        margin-right: 26px;
        border-radius: 16px;
        height: 100%;
        .calendar-header{
          margin-bottom: 16px;
          .year-month-selector{
            display: flex;
            gap: 8px;
            .date-select{
              width: 95px;
              height: 30px;
            }
          }
        }
        .calendar-grid{
          .calendar-weekdays{
            display: flex;
            margin-bottom: 8px;
            .weekday{
              flex: 1;
              text-align: center;
              font-size: 12px;
              color: #86909C;
              font-weight: 500;
              padding: 4px 0;
            }
          }
          .calendar-loading{
            display: flex;
            align-items: center;
            justify-content: center;
            height: 200px;
            color: #86909C;
            font-size: 14px;
          }
          .calendar-days{
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 2px;
            .calendar-day{
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 30px;
              width: 30px;
              border-radius: 50%;
              cursor: pointer;
              transition: all 0.2s ease;
              .day-number{
                font-weight: 400;
                font-size: 14px;
                color:#000000D9;
                line-height: 100%;
              }
              .exam-dot{
                position: absolute;
                bottom: 3px;
                left: 50%;
                transform: translateX(-50%);
                width: 4px;
                height: 4px;
                background: #E60012;
                border-radius: 50%;
              }
              &:hover{
                background: #F5F6F9;
              }
              &.is-today{
                //background: #E60012;
                .day-number{
                  //color: #FFFFFF;
                  font-weight: 600;
                }
                //.exam-dot{
                //  background: #FFFFFF;
                //}
              }
              &.is-selected{
                background:#FFE5E7;
                .day-number{
                  color: #000000D9;
                }
                .exam-dot{
                  background: #E60012;
                }
              }
              &:not(.is-current-month){
                .day-number{
                  color: #C9CDD4;
                }
                cursor: default;
                &:hover{
                  background: transparent;
                }
              }
            }
          }
        }
      }
      .exam-list-box{
        width: 378px;
        height: 100%;
        padding: 16px;
        box-sizing: border-box;
        background: #F5F6F9;
        border-radius: 12px;
        overflow-y: auto;
        .exam-list-empty{
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          img{
            width: 128px;
            height: 128px;
          }
          p{
            margin-top: 12px;
            color: rgba(0,0,0,0.9);
            font-size: 12px;
            font-weight: 500;
          }
        }
        .exam-list-item{
          padding: 12px 16px;
          width: 100%;
          box-sizing: border-box;
          border: 1px solid #E5E6EB;
          border-radius: 4px;
          background: #FFFFFF;
          margin-bottom: 12px;
          &:last-child{
            margin-bottom: 0;
          }
          .exam-title {
            color: #1D2129;
            display: flex;
            align-items: center;
            font-size: 14px;
            font-weight: 500;
            line-height: 21px;
            span{
              width: 16px;
              height: 16px;
              margin-right: 4px;
              background: url("@/common/images/student/ic_edit.png") center / contain no-repeat;
            }
          }
          .exam-time{
            color: #86909C;
            font-size: 12px;
            margin-top: 4px;
          }
        }
      }
    }
  }
}
</style>
<style lang="less">
.date-select{
  .el-input__inner{
    height: 30px;
    line-height: 30px;
  }
  .el-input__icon{
    line-height: 30px;
  }
}
</style>